import React, { useEffect, useState } from 'react';
import { Col, Row, Container } from 'reactstrap';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { get } from 'lodash-es';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  preformatFloat,
  locale,
  formatCurrencyForLocale
} from '../../../utilities/invoiceUtils';
import { useAxios } from '../../../hooks';
import { selectInvoiceSendData } from '../../../selectors/invoiceSelectors';
import { formatChannelText } from '../../../utilities/textsFormatters';

const SentInvoices = () => {
  const { invoiceGroupID, invoiceIDList } = useParams();
  const invoiceIDStringArray = invoiceIDList.split(',');
  const invoiceIDArray = invoiceIDStringArray.map(Number);
  const [invoiceData, setInvoiceData] = useState([]);
  const [invoiceTotals, setInvoiceTotals] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [allInvoiceTotal, setAllInvoiceTotal] = useState(0);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const { houseRef } = useSelector(state => state.house);
  const invoiceSendData = useSelector(selectInvoiceSendData);

  const { data: groupInfoData, isLoading: isInvoiceLoading } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef],
    { manual: false }
  );

  useEffect(() => {
    if (invoiceSendData) {
      setInvoiceData(get(invoiceSendData, 'invoiceConfirmations', []));
      setInvoiceTotals(get(invoiceSendData, 'invoiceConfirmationTotal', {}));
    }
  }, [invoiceSendData]);

  useEffect(() => {
    if (typeof groupInfoData === 'undefined') {
      return;
    }

    setInvoices(get(groupInfoData, 'data.data.invoiceBidders', []));
  }, [groupInfoData]);

  useEffect(() => {
    if (!invoices.length) {
      return;
    }

    let grandTotal = 0;
    invoices.forEach((invoice) => {
      if (invoiceIDArray.indexOf(invoice.invoiceID) > -1) {
        grandTotal += parseFloat(preformatFloat(invoice.invoiceTotal));
      }
    });

    setCurrencySymbol(invoices[0].totalPrice.currencySymbol);
    setAllInvoiceTotal(formatCurrencyForLocale(grandTotal));
  }, [invoices]);

  const getTotalPending = () => {
    let pendingTotal = 0;
    invoices.forEach((invoice) => {
      if (invoiceIDArray.indexOf(invoice.invoiceID) > -1) {
        pendingTotal += parseFloat(preformatFloat(invoice.outStandingBalance));
      }
    });

    const formattedTotal = pendingTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return `${currencySymbol}${formattedTotal}`;
  };

  if (isInvoiceLoading || !invoices.length) {
    return <ReactLoading className="loading-spinner" type="spin" color="black" />;
  }

  return (

    <Container>
      <Row>
        <Col xs={12} className="mt-5">
          <div className="alert alert-success" role="alert">
            <span className="alert-content">
              <FontAwesomeIcon icon="check-circle" className="" />
              <span className="pl-2">
                Your invoices have been sent
              </span>
            </span>
          </div>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col xs={12}>
          <Link
            aria-label="link to group details"
            className=""
            to={`/invoices/InvoiceGroupDetails/${invoiceGroupID}`}
          >
            &lt; Invoice Group Details
          </Link>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="mt-4">
          <h1>Sent Invoices</h1>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xs={2}>
          <div className="font-weight-bold">
            Total Invoices
          </div>
          <div className="ml-2">
            {invoiceData.length}
          </div>
        </Col>
        <Col xs={2}>
          <div className="font-weight-bold">
            Total Items
          </div>
          <div className="ml-2">
            {invoiceTotals.itemCount}
          </div>
        </Col>
        <Col xs={2}>
          <div className="font-weight-bold">
            Invoice Total
          </div>
          <div className="ml-2">
            {currencySymbol}{allInvoiceTotal}
          </div>
        </Col>
        <Col xs={6}>
          <div className="font-weight-bold">
            Total Pending Charges
          </div>
          <div className="ml-2">
            {getTotalPending()}
          </div>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <table className="w-100 border mt-3">
            <tbody>
              <tr>
                <td className="border py-1 px-3 font-weight-bold">
                  Invoice #
                </td>
                <td className="border py-1 px-3 font-weight-bold">
                  Name
                </td>
                <td className="border py-1 px-3 font-weight-bold">
                  Location
                </td>
                <td className="border py-1 px-3 font-weight-bold">
                  Outstanding Balance
                </td>
                <td className="border py-1 px-3 font-weight-bold">
                  Status
                </td>
              </tr>
              {invoiceData.map(invoice => (
                <tr key={invoice.invoiceID}>
                  <td className="border py-1 px-3">
                    {invoice.invoiceID}
                  </td>
                  <td className="border py-1 px-3">
                    {invoice.memberName}
                    {invoice.companyID && (
                    <span> ({formatChannelText(invoice.companyID)})</span>
                    )}
                  </td>
                  <td className="border py-1 px-3">
                    {invoice.location}
                  </td>
                  <td className="border py-1 px-3">
                    <div>
                      <div className="float-left ml-3">
                        {invoice.currencyCode}
                      </div>
                      <div className="float-right mr-4">
                        {formatCurrencyForLocale(invoice.outstandingBalance)}
                      </div>
                    </div>
                  </td>
                  <td className="border py-1 px-3">
                    {invoice.invoiceStatus}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Col>
      </Row>
    </Container>
  );
};
export default SentInvoices;
